import { useState } from 'react';
import {
  Icon,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@tutero/ui';

import {
  ensureCluster,
  navigation,
  offerableClusters,
  UNREADABLE,
  writeCluster,
  type Cluster,
} from '@/lib/cluster';

import { Flag } from './flags/Flag';
import { useStaff } from './session';

/** Each region by its country's name, the code beside it. */
const REGION_NAME: Record<Cluster, string> = {
  au: 'Australia',
  us: 'United States',
};

/** Flutter's own words (`lib/legacy/shared/widgets/dropdowns/avatar_menu.dart`). */
export const CLUSTER_FAILED =
  'Region selection has failed. Please report this as an issue.';

/**
 * The cluster the admin is reading, and the way to change it
 * (`manifests-draw-the-nav` D9, D10), as one-click tabs rather than a menu.
 * Changing it writes the cookie and reloads; nothing else happens in between.
 *
 * The cluster is settled once, on mount, so a re-render never rewrites the cookie.
 */
export function ClusterPicker() {
  const staff = useStaff();
  const [cluster] = useState(() => ensureCluster(staff.country));

  if (cluster === UNREADABLE) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            tabIndex={0}
            aria-label={CLUSTER_FAILED}
            className="text-destructive inline-flex items-center rounded-sm outline-none focus-visible:ring-2"
          >
            <Icon name="error" size={18} />
          </span>
        </TooltipTrigger>
        <TooltipContent>{CLUSTER_FAILED}</TooltipContent>
      </Tooltip>
    );
  }

  function choose(next: string) {
    if (next === cluster) return;
    writeCluster(next as Cluster);
    navigation.reload();
  }

  /*
   * The regions side by side, one click apart, the one being read lit and the other dimmed. Flags
   * alone, to keep the lockup its width in the nav's 260px; the country's name is the tooltip.
   * Choosing the lit one again does nothing (Radix would otherwise clear the group).
   */
  return (
    <ToggleGroup
      type="single"
      spacing={1}
      value={cluster}
      onValueChange={(next) => next && choose(next)}
      /* *Region* is what a staff member reads; *cluster* is only the code's word for it. */
      aria-label="Region"
      className="bg-sidebar-accent h-9 shrink-0 rounded-lg p-1"
    >
      {offerableClusters().map((each) => (
        <ToggleGroupItem
          key={each}
          value={each}
          aria-label={`${REGION_NAME[each]} (${each.toUpperCase()})`}
          title={REGION_NAME[each]}
          className="text-sidebar-foreground/70 hover:bg-sidebar/40 hover:text-sidebar-foreground focus-visible:ring-sidebar-ring data-[state=on]:bg-sidebar data-[state=on]:text-sidebar-foreground h-7 rounded-md px-2 data-[state=off]:opacity-50 hover:opacity-100 data-[state=on]:shadow-sm"
        >
          <Flag cluster={each} />
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
